/**
 * Billing (mission 9 WP11 T065; FR-003, FR-006, FR-007, FR-009 to FR-012; B.1, B.4, B.7; C-010;
 * contracts/billing-api.md; amendment 6; squad U17; OR-14).
 *
 * Typed wrappers for the billing routes. The types follow the example bodies in
 * `backend/internal/httpapi/testdata/m09/` member for member (their SPA copies are
 * `src/test/fixtures/m09/`, pinned by `fixtures-m09.test.ts`). Every price and limit the
 * screens render is a member of these bodies, never a literal (C-010).
 *
 * - Bodies are built key by key (C-006); a plan outside `PLAN_IDS` and a purchase `request_id`
 *   that is not a UUID are an `InvalidIdError`, and nothing is sent.
 * - Provider URLs (`checkout_url`, `card_update_url`, an invoice's `hosted_url`) are capability
 *   URLs: they go through `safeProviderUrl` and `leaveForProvider` only, never into state, the
 *   DOM, an Error or a log.
 * - The statement is not fetched here: `statementFile` names it and `lib/files.ts`
 *   `downloadStatement` fetches it with credentials and saves it through a blob (OR-14, FR-026).
 */
import { InvalidIdError, api, oneOf } from '../api'

/** The derived access (research §1.3; amendment 7). */
export type BillingState = 'pending_subscription' | 'active' | 'dunning' | 'read_only'
export const BILLING_STATES: readonly BillingState[] = ['pending_subscription', 'active', 'dunning', 'read_only']

/** Why access is limited: null while active. */
export type BillingReason = 'no-subscription' | 'dunning' | 'cancelled'

/** What restores access. */
export type BillingRemedy = 'subscribe' | 'update-payment'

export type PlanId = 'monthly' | 'annual'
export const PLAN_IDS: readonly PlanId[] = ['monthly', 'annual']

/** GET /v1/billing's access member. The dunning members are null outside dunning. */
export interface BillingAccessView {
  state: BillingState
  reason: BillingReason | null
  remedy: BillingRemedy | null
  dunning_day: number | null
  dunning_days_left: number | null
  read_only_since: string | null
  /**
   * When read-only begins or began. Inside the dunning window it is a future instant, so it is never
   * rendered as the date read-only began; that is `read_only_since`.
   */
  read_only_at: string | null
  cancelling: boolean
}

/** The current plan and a scheduled switch (FR-010). */
export interface BillingPlanView {
  id: PlanId
  price_cents: number
  interval: 'month' | 'year'
  pending_switch: PlanId | null
  switch_at: string | null
}

/** The current credit cycle (B.2). */
export interface BillingCycle {
  start: string
  end: string
  first_cycle: boolean
}

/** The first-cycle soft cap (B.5). */
export interface SoftCap {
  active: boolean
  forgiven: number
  ceiling: number
  closes_at: string | null
}

/** The balance and its buckets: balance = included + purchased. */
export interface BillingCredits {
  balance: number
  included_remaining: number
  purchased_remaining: number
  reserved: number
}

/** A purchased block (B.4); `expiring` is inside the expiring notice. */
export interface CreditBlock {
  id: string
  credits: number
  remaining: number
  amount_cents: number
  purchased_at: string
  expires_at: string
  expiring: boolean
}

/** A purchasable tier (B.4). */
export interface CreditTier {
  credits: number
  cents_per_1000: number
  price_cents: number
}

export interface PlanPrices {
  monthly_cents: number
  annual_cents: number
}

/** The terms the screens state, every figure from the configuration source (C-010). */
export interface BillingTerms {
  allowance_per_cycle: number
  forgiveness_ceiling: number
  assistant_ceiling: number
  block_ttl_months: number
  expiring_notice_days: number
  dunning_days: number
  cancel_terms_version: string
}

/** The card on file: brand and last four only. */
export interface CardOnFile {
  brand: string
  last4: string
}

/** A provider invoice. `hosted_url` is a capability URL (safeProviderUrl only). */
export interface Invoice {
  id: string
  status: string
  amount_cents: number
  period_start: string
  period_end: string
  paid_at: string | null
  hosted_url: string
}

/** A cycle's usage statement, named by its full cycle start; no URL (OR-14). */
export interface Statement {
  cycle_start: string
}

/** GET /v1/billing (FR-011), and the body of cancel, undo and the plan switch. It answers every state. */
export interface BillingView {
  access: BillingAccessView
  plan: BillingPlanView | null
  renews_at: string | null
  period_end: string | null
  cycle: BillingCycle | null
  soft_cap: SoftCap
  credits: BillingCredits
  blocks: CreditBlock[]
  tiers: CreditTier[]
  prices: PlanPrices
  terms: BillingTerms
  card: CardOnFile | null
  billing_email: string | null
  invoices: Invoice[]
  statements: Statement[]
  provider_unavailable: boolean
}

/**
 * GET /v1/credits's additive `billing` member (amendment 6; research §1.19). Optional on `Credits`:
 * an absent member reads as `active` (U17); the server is the control.
 */
export interface BillingAccess {
  state: BillingState
  reason: BillingReason | null
  remedy: BillingRemedy | null
  plan: PlanId | null
  dunning_day: number | null
  dunning_days_left: number | null
  read_only_since: string | null
  /** A future instant inside the dunning window; never rendered as the date read-only began. */
  read_only_at: string | null
  cancel_at_period_end: boolean
  period_end: string | null
  included_remaining: number
  purchased_remaining: number
}

export interface CheckoutBody {
  plan: PlanId
  cancel_terms_version: string
  billing_email: string
}

export interface CheckoutResult {
  checkout_url: string
}

export interface PurchaseBody {
  credits: number
  request_id: string
}

/** POST /v1/credits/purchase's 201 (200 on a repeat with the same request id and size). */
export interface PurchaseResult {
  block: CreditBlock
  credits: BillingCredits
}

export interface CardUpdateResult {
  card_update_url: string
}

export interface BillingEmailResult {
  billing_email: string
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/** A purchase's idempotency key, one per dialog open (OR-17). */
function requestId(id: string): string {
  if (!UUID.test(id)) throw new InvalidIdError('request id')
  return id
}

/**
 * The statement's file name (FR-012, C15): the full UTC cycle start, `20260928T150405Z.csv`, which
 * matches the route's `^\d{8}T\d{6}Z\.csv$`. Anything that is not a timestamp is an `InvalidIdError`.
 */
export function statementFile(cycleStart: string): string {
  const time = Date.parse(cycleStart)
  if (Number.isNaN(time)) throw new InvalidIdError('cycle start')
  const iso = new Date(time).toISOString() // 2026-09-28T15:04:05.000Z
  return `${iso.slice(0, 19).replace(/[-:]/g, '')}Z.csv`
}

const post = (body: object): RequestInit => ({ method: 'POST', body: JSON.stringify(body) })

export async function getBilling(): Promise<BillingView> {
  return api<BillingView>('/billing')
}

export async function startCheckout(body: CheckoutBody): Promise<CheckoutResult> {
  const clean: CheckoutBody = {
    plan: oneOf(body.plan, PLAN_IDS, 'plan'),
    cancel_terms_version: body.cancel_terms_version,
    billing_email: body.billing_email,
  }
  return api<CheckoutResult>('/billing/checkout', post(clean))
}

export async function purchaseCredits(body: PurchaseBody): Promise<PurchaseResult> {
  const clean: PurchaseBody = { credits: body.credits, request_id: requestId(body.request_id) }
  return api<PurchaseResult>('/credits/purchase', post(clean))
}

export async function cancelPlan(): Promise<BillingView> {
  return api<BillingView>('/billing/cancel', post({}))
}

export async function undoCancel(): Promise<BillingView> {
  return api<BillingView>('/billing/cancel/undo', post({}))
}

export async function switchPlan(plan: PlanId): Promise<BillingView> {
  return api<BillingView>('/billing/plan', post({ plan: oneOf(plan, PLAN_IDS, 'plan') }))
}

export async function startCardUpdate(): Promise<CardUpdateResult> {
  return api<CardUpdateResult>('/billing/card-update', post({}))
}

export async function putBillingEmail(billingEmail: string): Promise<BillingEmailResult> {
  return api<BillingEmailResult>('/billing/email', { method: 'PUT', body: JSON.stringify({ billing_email: billingEmail }) })
}
